<template>
  <div class="delivery-man-info">
    <!-- En-tête informations personnelles avec accessibilité -->
    <div
      class="info-header mb-4"
      aria-label="En-tête informations personnelles du livreur"
    >
      <h2
        class="section-title"
        aria-label="Informations personnelles du livreur"
      >
        <feather-icon
          icon="UserIcon"
          size="20"
          class="mr-2 text-primary"
          aria-hidden="true"
        />
        Informations personnelles du livreur
      </h2>
    </div>

    <!-- Section profil principal -->
    <div
      class="profile-main mb-4"
      aria-label="Profil principal du livreur"
    >
      <b-media
        no-body
        class="align-items-center"
      >
        <b-media-aside class="mr-3">
          <b-avatar
            :src="deliveryMan.avatar || null"
            size="4rem"
            variant="light-primary"
            class="shadow-sm"
            :aria-label="'Avatar du livreur ' + deliveryMan.username"
          >
            <feather-icon
              icon="UserIcon"
              size="24"
              aria-hidden="true"
            />
          </b-avatar>
        </b-media-aside>
        <b-media-body>
          <h3
            class="mb-1 font-weight-bold"
            aria-label="Nom du livreur"
          >
            {{ deliveryMan.username }}
          </h3>
          <div class="d-flex align-items-center mb-2">
            <b-badge
              :variant="deliveryMan.is_active ? 'danger' : 'success'"
              pill
              class="mr-2"
              :aria-label="'Statut du livreur : ' + (deliveryMan.is_active ? 'Actif' : 'Inactif')"
            >
              {{ deliveryMan.is_active ? 'Actif' : 'Inactif' }}
            </b-badge>
          </div>
          <small
            class="text-muted"
            aria-label="Date d'inscription du livreur"
          >
            Membre depuis {{ formatDate(deliveryMan.created_at) }}
          </small>
        </b-media-body>
      </b-media>
    </div>

    <!-- Grille d'informations complémentaires -->
    <div
      class="info-grid"
      aria-label="Informations complémentaires du livreur"
    >
      <!-- Email -->
      <div class="info-item">
        <div class="info-icon">
          <feather-icon
            icon="MailIcon"
            size="18"
          />
        </div>
        <div class="info-content">
          <div class="info-label">
            Email
          </div>
          <div class="info-value">
            {{ deliveryMan.email || 'Non défini' }}
          </div>
        </div>
      </div>

      <!-- Phone -->
      <div class="info-item">
        <div class="info-icon">
          <feather-icon
            icon="PhoneIcon"
            size="18"
          />
        </div>
        <div class="info-content">
          <div class="info-label">
            téléphone professionnel
          </div>
          <div class="info-value">
            {{ deliveryMan.phone || 'Non défini' }}
          </div>
        </div>
      </div>

      <!-- Phone 2 -->
      <div class="info-item">
        <div class="info-icon">
          <feather-icon
            icon="PhoneIcon"
            size="18"
            class="text-warning"
          />
        </div>
        <div class="info-content">
          <div class="info-label">
            téléphone personnel
          </div>
          <div class="info-value">
            {{ deliveryMan.phone2 || 'Non défini' }}
          </div>
        </div>
      </div>

      <!-- Address -->
      <div class="info-item">
        <div class="info-icon">
          <feather-icon
            icon="MapPinIcon"
            size="18"
          />
        </div>
        <div class="info-content">
          <div class="info-label">
            Adresse
          </div>
          <div class="info-value">
            {{ deliveryMan.address || 'Non défini' }}
          </div>
        </div>
      </div>

      <!-- Repository -->
      <div class="info-item">
        <div class="info-icon">
          <feather-icon
            icon="HomeIcon"
            size="18"
          />
        </div>
        <div class="info-content">
          <div class="info-label">
            Dépôt
          </div>
          <div class="info-value">
            {{ deliveryMan.repository_name || 'Non défini' }}
          </div>
        </div>
      </div>

      <!-- Vehicle -->
      <div
        v-if="deliveryMan.car_details"
        class="info-item"
      >
        <div class="info-icon">
          <feather-icon
            icon="TruckIcon"
            size="18"
          />
        </div>
        <div class="info-content">
          <div class="info-label">
            Véhicule
          </div>
          <div class="info-value">
            {{
              deliveryMan.car_details ?
                `${deliveryMan.car_details.brand} ${deliveryMan.car_details.model || ''}`.trim() :
                'Non défini'
            }}
          </div>
          <div
            v-if="deliveryMan.car_details && deliveryMan.car_details.serial_number"
            class="d-flex align-items-center justify-content-between mt-1"
          >
            <small class="text-muted">
              Série: {{ deliveryMan.car_details.serial_number }}
            </small>
            <div v-if="deliveryMan.payment_info">
              <small class="text-muted mr-2">Propriétaire du Véhicule</small>
              <b-badge
                :variant="deliveryMan.payment_info.owns_vehicle ? 'success' : 'secondary'"
                pill
              >
                {{ deliveryMan.payment_info.owns_vehicle ? 'Oui' : 'Non' }}
              </b-badge>
            </div>
          </div>
        </div>
      </div>

      <!-- Registration Date -->
      <div class="info-item">
        <div class="info-icon">
          <feather-icon
            icon="CalendarIcon"
            size="18"
          />
        </div>
        <div class="info-content">
          <div class="info-label">
            Date d'inscription
          </div>
          <div class="info-value">
            {{ formatDate(deliveryMan.created_at) }}
          </div>
        </div>
      </div>

      <!-- Status -->
      <div class="info-item">
        <div class="info-icon">
          <feather-icon
            :icon="deliveryMan.is_active ? 'CheckCircleIcon' : 'XCircleIcon'"
            size="18"
          />
        </div>
        <div class="info-content">
          <div class="info-label">
            Statut du compte
          </div>
          <div class="info-value">
            <b-badge
              :variant="deliveryMan.is_active ? 'danger' : 'success'"
              pill
            >
              {{ deliveryMan.is_active ? 'Actif' : 'Inactif' }}
            </b-badge>
          </div>
        </div>
      </div>
    </div>

    <!-- Payment Information Section -->
    <div
      v-if="deliveryMan.payment_info"
      class="info-header mt-4 mb-3"
    >
      <h2 class="section-title">
        <feather-icon
          icon="DollarSignIcon"
          size="20"
          class="mr-2 text-success"
          aria-hidden="true"
        />
        Informations de Paiement
      </h2>
    </div>

    <div
      v-if="deliveryMan.payment_info"
      class="info-grid"
    >
      <!-- Payment Type -->
      <div class="info-item">
        <div class="info-icon">
          <feather-icon
            icon="CreditCardIcon"
            size="18"
          />
        </div>
        <div class="info-content">
          <div class="info-label">
            Type de Paiement
          </div>
          <div class="info-value">
            <b-badge
              :variant="deliveryMan.payment_info.pay_type === 'salary' ? 'primary' : 'info'"
              pill
            >
              {{ deliveryMan.payment_info.pay_type_display || 'Non défini' }}
            </b-badge>
          </div>
        </div>
      </div>

      <!-- Compensation Amount -->
      <div class="info-item">
        <div class="info-icon">
          <feather-icon
            icon="DollarSignIcon"
            size="18"
          />
        </div>
        <div class="info-content">
          <div class="info-label">
            {{ deliveryMan.payment_info.pay_type === 'salary' ? 'Salaire Mensuel' : 'Taux par Colis' }}
          </div>
          <div class="info-value">
            {{ deliveryMan.payment_info.compensation ? deliveryMan.payment_info.compensation.toFixed(3) + ' DT' : 'Non défini' }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  BMediaBody, BMedia, BMediaAside, BAvatar, BBadge,
} from 'bootstrap-vue'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'

export default {
  components: {
    BMediaBody,
    BMedia,
    BMediaAside,
    BAvatar,
    BBadge,
  },
  props: {
    deliveryMan: {
      type: Object,
      required: true,
    },
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return 'Non défini'
      return format(new Date(dateString), 'dd MMMM yyyy', { locale: fr })
    },
  },
}
</script>

<style lang="scss" scoped>
.delivery-man-info {
  padding: 1rem;
}

.info-header {
  .section-title {
    display: flex;
    align-items: center;
    margin-bottom: 0;
    color: #5e5873;
    font-weight: 600;
  }
}

.profile-main {
  padding: 1.5rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-radius: 0.5rem;
  border-left: 4px solid #ff0000;
}

.info-grid {
  display: grid;
  gap: 1rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
}

.info-item {
  display: flex;
  align-items: flex-start;
  padding: 1rem;
  background: white;
  border-radius: 0.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  }

  .info-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    background: #f8f9fa;
    border-radius: 0.357rem;
    margin-right: 1rem;
    flex-shrink: 0;
    color: #ff0000;
  }

  .info-content {
    flex: 1;
    min-width: 0;

    .info-label {
      font-size: 0.75rem;
      font-weight: 600;
      color: #6e6b7b;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: 0.25rem;
    }

    .info-value {
      font-size: 0.875rem;
      font-weight: 500;
      color: #5e5873;
      word-break: break-word;
    }
  }
}

// Responsive adjustments
@media (max-width: 767px) {
  .info-grid {
    grid-template-columns: 1fr;
  }

  .profile-main {
    padding: 1rem;
  }

  .delivery-man-info {
    padding: 0.5rem;
  }
}
</style>
